import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Button,
  makeStyles,
} from '@material-ui/core'
import TableMultiSelectMenu from 'app/components/TableMultiSelectMenu'
import effects from 'app/effects/effects'
import { useTableFetch } from 'commons/reactTableFetch'
import useTableDelete from 'commons/useTableDelete'
import * as R from 'ramda'
import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import RoleFormDialog from './RoleFormDialog'
import RolesPermissionsDeletingDialog from './RolesDeletingDialog'
import { useRoleTable } from './useRoleTable'
import { Label } from './Constants'
import { exportBeautifulPDF } from 'app/utilize/generate-pdf'
import { exportCsv } from 'app/utilize/generate-csv'
import EmptyStateView from 'app/components/EmptyStateView'
import Assets from 'app/configs/assetConfigs'
import clsx from 'clsx'
import FetchTable from 'app/components/table/FetchTable'
import {
  withFirstColumn,
  withDeleteColumn,
} from 'app/components/table/TableColumn'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import CancelButton from 'app/components/buttons/CancelButton'
import { useExportDialog } from 'commons/useExportDialog'
import { useIsPaidPlan } from 'app/auth/paidPlanAccess'
import DeleteMenuItem from 'app/components/table/DeleteMenuItem'
import { useConversationSocket } from 'app/main/messages/conversations/useConversationSocket'
import { SocketEvent } from 'app/main/messages/conversations/socket'

const useStyles = makeStyles((theme) => ({
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  normalText: {
    color: theme.palette.textHighlight.main,
  },
  deleteMenu: {
    color: theme.palette.negative.main,
  },
  trHover: {
    '& .rt-tr-group': {
      padding: '0px !important',
    },
    '& .rt-tr': {
      padding: '7px 0 !important',
    },
  },
}))

const fetchData = (page) => {
  return effects.apis.getRoles({
    page: page.currentPage,
    limit: page.pageSize,
    sort: R.compose(
      R.when(
        R.allPass([R.o(R.not, R.isNil), R.has('desc'), R.has('id')]),
        ({ id, desc }) => `${id}:${desc === true ? 'DESC' : 'ASC'}`,
      ),
      R.when(
        R.allPass([R.o(R.not, R.isNil), R.propEq('id', 'type')]),
        R.evolve({ desc: R.not }),
      ),
      R.head,
      R.prop('sorted'),
    )(page),
  })
}

const RolesPermissionsTable = ({ onAddRolesPermissions }) => {
  const classes = useStyles()
  const isPaidPlan = useIsPaidPlan()
  const { formatMessage } = useIntl()
  const { handleRecordUpdates } = useConversationSocket()
  const {
    pageInfo,
    onFetchData,
    refetchData,
    startFetchData,
    endFetchData,
    toggleSelect,
    toggleSelectAll,
    showErrorDialogue,
    hasData,
    showPagination,
    defaultPageSize,
  } = useTableFetch(fetchData)

  const { checkedData, checkedIds, isCheckedAll, isIndeterminated } = pageInfo

  const { editingData, openUpdatingRoleDialog, closeRoleFormDialog } =
    useRoleTable()

  const onDeleteSuccessPromise = () => {
    startFetchData()
    return refetchData().then(endFetchData)
  }

  const {
    afterConfirmPending,
    pendingDisplayItems,
    onConfirmDelete,
    onCancelDelete,
    handlerDeleteAll,
    handlerDeleteOne,
    isOpenDeleteConfirmation,
  } = useTableDelete({
    asyncDelete: effects.apis.deleteRoles,
    onDeleteSuccessPromise,
    showErrorDialogue,
    getCheckedData: () => checkedData,
  })

  const handleSubmitRoleForm = async (data) => {
    startFetchData()

    try {
      if (R.has('id', data)) {
        await effects.apis.updateRole(data)
        handleRecordUpdates(SocketEvent.UPDATE_ROLE, data.id)
      } else {
        await effects.apis.createRole(data)
      }
      await closeRoleFormDialog()
      await refetchData()
    } catch (error) {
      showErrorDialogue(error)
    } finally {
      endFetchData()
    }
  }

  const {
    title,
    message,
    isOpenExportDialog,
    isCSV,
    isPDF,
    actionTypeDialog,
    onShowDialogExport,
  } = useExportDialog()

  function exportCSV() {
    let headerCVS = [
      {
        label: formatMessage({ id: 'rolesPermissions.table.no.label' }),
        key: 'row',
      },
      {
        label: formatMessage({ id: 'rolesPermissions.table.role.label' }),
        key: 'name',
      },
      {
        label: formatMessage({ id: 'text.type' }),
        key: 'type',
      },
      {
        label: formatMessage({
          id: 'rolesPermissions.table.teamMembers.label',
        }),
        key: 'usersCount',
      },
    ]

    exportCsv({
      headerCVS,
      dataCSV: generateDataExport(),
      fileName: formatMessage({ id: 'rolesPermissions.header.label' }),
    })

    toggleSelectAll(false)

    onShowDialogExport({
      type: actionTypeDialog.CLOSE_EXPORT_DIALOG,
    })
  }

  function exportPDF() {
    let headerPDF = [
      {
        header: formatMessage({ id: 'rolesPermissions.table.no.label' }),
      },
      {
        header: formatMessage({ id: 'rolesPermissions.table.role.label' }),
      },
      {
        header: formatMessage({ id: 'text.type' }),
      },
      {
        header: formatMessage({
          id: 'rolesPermissions.table.teamMembers.label',
        }),
      },
    ]

    const columnStyles = {
      0: { cellWidth: 40, halign: 'center' },
      1: { cellWidth: 125 },
      3: { cellWidth: 75, halign: 'center' },
    }
    const headStyles = {
      halign: 'center',
    }
    exportBeautifulPDF({
      header: headerPDF,
      body: generateDataExport(),
      fileName: formatMessage({
        id: 'rolesPermissions.header.label',
      }),
      headStyles,
      columnStyles,
    })

    toggleSelectAll(false)

    onShowDialogExport({
      type: actionTypeDialog.CLOSE_EXPORT_DIALOG,
    })
  }

  const generateDataExport = () => {
    return checkedData.map((value, index) => {
      return {
        row: ++index,
        name: value.name,
        type: formatMessage({ id: Label[value.type] }), //
        usersCount: value.usersCount,
      }
    })
  }

  const exportFile = () => {
    return (isCSV && exportCSV()) || (isPDF && exportPDF())
  }

  const renderTableMenu = () => (
    <TableMultiSelectMenu>
      {(closeMenu) => (
        <MenuList>
          <MenuItem
            onClick={() => {
              closeMenu()
              onShowDialogExport({
                type: actionTypeDialog.OPEN_EXPORT_PDF_DIALOG,
                title: formatMessage({ id: 'action.exportPDF' }),
                message: formatMessage({
                  id: 'rolesPermissions.dialog.exportPDF.desc',
                }),
              })
            }}
          >
            <ListItemIcon className="min-w-40">
              <img alt="Export PDF" src={Assets.iconPDF} />
            </ListItemIcon>
            <ListItemText
              className={clsx(classes.normalText)}
              primary={formatMessage({
                id: 'rolesPermissions.table.action.exportPDF.label',
              })}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeMenu()
              onShowDialogExport({
                type: actionTypeDialog.OPEN_EXPORT_CSV_DIALOG,
                title: formatMessage({ id: 'action.exportCSV' }),
                message: formatMessage({
                  id: 'rolesPermissions.dialog.exportCSV.desc',
                }),
              })
            }}
          >
            <ListItemIcon className="min-w-40">
              <img alt="Export CSV" src={Assets.iconCSV} />
            </ListItemIcon>
            <ListItemText
              className={clsx(classes.normalText)}
              primary={formatMessage({
                id: 'rolesPermissions.table.action.exportCSV.label',
              })}
            />
          </MenuItem>
          <DeleteMenuItem
            onClick={() => {
              closeMenu()
              handlerDeleteAll()
            }}
            selectedCount={R.length(checkedIds)}
          />
        </MenuList>
      )}
    </TableMultiSelectMenu>
  )

  return (
    <div className="flex flex-col flex-auto h-full overflow-hidden">
      {!hasData && (
        <EmptyStateView
          title={
            <FormattedMessage id="rolesPermissions.table.emptyState.title" />
          }
        >
          <Button
            color="secondary"
            variant="contained"
            onClick={onAddRolesPermissions}
          >
            <FormattedMessage id="rolesPermissions.table.emptyState.add.button" />
          </Button>
        </EmptyStateView>
      )}
      {hasData && (
        <FetchTable
          isClickRowDisable={!isPaidPlan}
          onClickRow={(row) => openUpdatingRoleDialog(row)}
          FilterComponent={null}
          pages={pageInfo.total}
          data={pageInfo.data}
          total={pageInfo.totalData}
          error={pageInfo.error}
          refetchData={refetchData}
          defaultPageSize={defaultPageSize}
          manual
          loading={pageInfo.isLoading}
          onFetchData={onFetchData}
          noDataText={formatMessage({
            id: 'rolesPermissions.table.empty.label',
          })}
          columns={[
            withFirstColumn({
              Header: () => (
                <div>
                  <Checkbox
                    color="secondary"
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                    onChange={(event) => {
                      toggleSelectAll(event.target.checked)
                    }}
                    checked={isCheckedAll}
                    indeterminate={isIndeterminated}
                  />
                  {!R.isEmpty(checkedIds) && renderTableMenu()}
                </div>
              ),
              Cell: (row) => {
                return (
                  <Checkbox
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                    color="secondary"
                    checked={checkedIds.includes(row.value.id)}
                    onChange={() => {
                      toggleSelect(row.value.id)
                    }}
                  />
                )
              },
            }),
            {
              Header: formatMessage({
                id: 'rolesPermissions.table.role.label',
              }),
              accessor: 'name',
              filterable: true,
              type: 'text',
            },
            {
              Header: formatMessage({
                id: 'text.type',
              }),
              accessor: 'type',
              filterable: true,
              type: 'text',
              Cell: (row) => {
                return <FormattedMessage id={Label[row.value]} />
              },
            },
            {
              Header: formatMessage({
                id: 'rolesPermissions.table.teamMembers.label',
              }),
              accessor: 'usersCount',
              sortable: false,
              filterable: true,
              type: 'text',
            },
            withDeleteColumn(handlerDeleteOne, true, undefined, !isPaidPlan),
          ]}
          showPagination={showPagination}
        />
      )}
      <RolesPermissionsDeletingDialog
        isPending={afterConfirmPending}
        deletingRoles={pendingDisplayItems}
        open={isOpenDeleteConfirmation}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />
      <RoleFormDialog
        isOpen={editingData !== undefined}
        initialData={editingData}
        onClose={closeRoleFormDialog}
        onSubmit={handleSubmitRoleForm}
      />
      <ConfirmationDialog
        open={isOpenExportDialog}
        title={title}
        body={message}
        CancelButton={
          <CancelButton
            onClick={() =>
              onShowDialogExport({
                type: actionTypeDialog.CLOSE_EXPORT_DIALOG,
              })
            }
          />
        }
        ConfirmButton={
          <PrimaryButton
            onClick={() => {
              exportFile()
            }}
          >
            <FormattedMessage id="action.export" />
          </PrimaryButton>
        }
      />
    </div>
  )
}

export default RolesPermissionsTable
